import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Controller, useFormContext } from 'react-hook-form'
import { Grid } from '@material-ui/core'
import SydLabel from '../../../../commonDesign/SydLabel'
import SydSelect from '../../../../commonDesign/SydSelect'
import SydInput from '../../../../commonDesign/SydInput'
import Switch from '../../../../molecules/Switch'
import SydSwitch from '../../../../commonDesign/SydSwitch'
import { IconPickerField } from '../../../../commonDesign/IconPicker'
import presets from './presets'

// Wealthbox `/v1/tasks` `task_type` enum.
const taskTypeOptions = [
  { label: 'All (default)', value: '' },
  { label: 'Parents only', value: 'parents' },
  { label: 'Subtasks only', value: 'subtasks' }
]

const presetOptions = [
  { label: 'Select a preset...', value: '' },
  ...Object.entries(presets).map(([key, preset]) => ({
    label: preset.displayName,
    value: key
  }))
]

// Scope option set per preset domain. My Book is hidden on note/event
// presets because Wealthbox's /v1/notes and /v1/events endpoints have no
// `created_by` or `assigned_to` filter; the server would silently drop the
// translation and the user would think they were seeing "my" items when
// they're actually seeing everything in the firm. Better to not offer the
// option at all than to lie.
const SCOPE_OPTIONS_BY_DOMAIN = {
  tasks: [
    { label: 'Client — tasks linked to the current client', value: 'client' },
    { label: 'My Book — all tasks assigned to me', value: 'user' },
    { label: 'All — every task in the workspace (no automatic binding)', value: 'all' }
  ],
  notes: [
    { label: 'Client — notes linked to the current client', value: 'client' },
    { label: 'All — every note in the workspace (Wealthbox has no per-user notes filter)', value: 'all' }
  ],
  events: [
    { label: 'Client — events linked to the current client', value: 'client' },
    { label: 'All — every event in the workspace (Wealthbox has no per-user events filter)', value: 'all' }
  ]
}

const createdByScopeOptions = [
  { label: 'Anyone (default)', value: '' },
  { label: 'Tasks I created', value: 'me' }
]

const styleVariantOptions = [
  { label: 'Default (Medium)', value: 'default' },
  { label: 'Small', value: 'sm' },
  { label: 'Medium', value: 'md' },
  { label: 'Large', value: 'lg' }
]

const formModeOptions = [
  { label: 'Simple (date only)', value: 'simple' },
  { label: 'Calendar (full edit form)', value: 'calendar' }
]

const sectionHeading = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: 'var(--text-color-subtle)',
  borderBottom: '1px solid var(--color-neutral-200)',
  paddingBottom: '4px'
}

function WealthboxListConfigEditor ({ fieldPrefix }) {
  const { control, watch } = useFormContext()
  const configPrefix = `${fieldPrefix}.config`

  const presetValue = watch(`${configPrefix}.preset`)
  const hideTitleValue = watch(`${configPrefix}.hideTitle`)
  const hideIconValue = watch(`${configPrefix}.hideIcon`)

  const selectedPreset = useMemo(
    () => (presetValue ? presets[presetValue] : null),
    [presetValue]
  )

  const presetUsesField = (field) => selectedPreset?.usesFields?.includes(field) ?? false
  const showUpdatedWithin = presetUsesField('updatedWithinDays')
  const showStaleAfter = presetUsesField('staleAfterDays')
  const showLookahead = presetUsesField('lookaheadDays')
  const showOrder = presetUsesField('order')

  // Some filter knobs only apply to specific entity domains. The notes
  // preset doesn't have completed / task_type / teamId server-side; the
  // events preset doesn't either; the task presets don't have the
  // preview-length display knob.
  const presetDomain = selectedPreset?.defaults?.domain || 'tasks'
  const isNotesPreset = presetDomain === 'notes'
  const isEventsPreset = presetDomain === 'events'
  const showTaskFilters = presetDomain === 'tasks'
  const showPreviewLength = isNotesPreset

  // Per-domain `order` enum from Wealthbox docs:
  //   - notes:  asc | created | updated
  //   - events: asc | desc | recent | created
  //   - tasks:  no order param exists (omitted)
  const orderOptions = isNotesPreset
    ? [
      { label: 'Ascending (oldest first)', value: 'asc' },
      { label: 'Created (newest first)', value: 'created' },
      { label: 'Recently updated (default)', value: 'updated' }
    ]
    : isEventsPreset
      ? [
        { label: 'Ascending (earliest start first, default)', value: 'asc' },
        { label: 'Descending (latest start first)', value: 'desc' },
        { label: 'Most recent', value: 'recent' },
        { label: 'Created (newest first)', value: 'created' }
      ]
      : []

  return (
    <Grid container spacing={2}>
      {/* Core */}
      <Grid item xs={12}>
        <div style={sectionHeading}>Core</div>
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.preset`}
          control={control}
          render={({ field }) => (
            <SydLabel
              required
              label='Preset'
              description='Which Wealthbox task set this list displays'
            >
              <SydSelect
                options={presetOptions}
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value || null)}
              />
            </SydLabel>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Controller
          name={`${configPrefix}.scope`}
          control={control}
          render={({ field }) => (
            <SydLabel label='Scope' description='Filter to a specific client, the signed-in advisor (tasks only), or firm-wide'>
              <SydSelect
                options={SCOPE_OPTIONS_BY_DOMAIN[presetDomain] || SCOPE_OPTIONS_BY_DOMAIN.tasks}
                value={field.value || 'client'}
                onChange={(e) => field.onChange(e.target.value || null)}
              />
            </SydLabel>
          )}
        />
      </Grid>

      {/* Appearance */}
      <Grid item xs={12}>
        <div style={sectionHeading}>Appearance</div>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={2}>
            <Controller
              name={`${configPrefix}.hideTitle`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Show title'>
                  <div style={{ display: 'flex', alignItems: 'center', minHeight: '54px', width: '100%', minWidth: 0 }}>
                    <Switch
                      checked={!field.value}
                      onChange={(e) => field.onChange(!e.target.checked)}
                    />
                  </div>
                </SydLabel>
              )}
            />
          </Grid>
          {!hideTitleValue && (
            <Grid item xs={6}>
              <Controller
                name={`${configPrefix}.title`}
                control={control}
                render={({ field }) => (
                  <SydLabel label='Title'>
                    <div style={{ width: '100%', minWidth: 0 }}>
                      <SydInput
                        {...field}
                        value={field.value || ''}
                        placeholder={selectedPreset?.defaults?.title || 'Title'}
                        minWidth='0'
                      />
                    </div>
                  </SydLabel>
                )}
              />
            </Grid>
          )}
          <Grid item xs={2}>
            <Controller
              name={`${configPrefix}.hideIcon`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Show icon'>
                  <div style={{ display: 'flex', alignItems: 'center', minHeight: '54px', width: '100%', minWidth: 0 }}>
                    <Switch
                      checked={!field.value}
                      onChange={(e) => field.onChange(!e.target.checked)}
                    />
                  </div>
                </SydLabel>
              )}
            />
          </Grid>
          {!hideIconValue && (
            <Grid item xs={2}>
              <Controller
                name={`${configPrefix}.icon`}
                control={control}
                render={({ field }) => (
                  <SydLabel label='Icon'>
                    <div style={{ width: '100%', minWidth: 0 }}>
                      <IconPickerField
                        value={field.value || selectedPreset?.defaults?.icon || null}
                        onChange={field.onChange}
                        placeholder={selectedPreset?.defaults?.icon || 'Select an icon...'}
                      />
                    </div>
                  </SydLabel>
                )}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Controller
              name={`${fieldPrefix}.styleVariant`}
              control={control}
              render={({ field }) => (
                <SydLabel label='Style Variant'>
                  <SydSelect
                    options={styleVariantOptions}
                    value={field.value || 'default'}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                </SydLabel>
              )}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* Capabilities + Filtering side-by-side */}
      <Grid item xs={12}>
        <div style={{ display: 'flex', gap: '32px' }}>
          <div style={{ width: '240px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={sectionHeading}>Capabilities</div>
            <Controller
              name={`${configPrefix}.enableCreate`}
              control={control}
              render={({ field }) => (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                  <span className='font-size-sm'>Allow creating</span>
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </div>
              )}
            />
            <Controller
              name={`${configPrefix}.enableEdit`}
              control={control}
              render={({ field }) => (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
                  <span className='font-size-sm'>Allow editing</span>
                  <Switch
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                </div>
              )}
            />
            {/* formMode only meaningful for entities with a multi-field edit
                form behind a simple/calendar split. Notes are content + category
                regardless. */}
            {!isNotesPreset && (
              <Controller
                name={`${configPrefix}.formMode`}
                control={control}
                render={({ field }) => (
                  <SydLabel
                    label='Form Mode'
                    description='Simple: name + due date only. Calendar: full edit form (priority, category, description).'
                  >
                    <SydSelect
                      minWidth={0}
                      options={formModeOptions}
                      value={field.value || 'simple'}
                      onChange={(e) => field.onChange(e.target.value || null)}
                    />
                  </SydLabel>
                )}
              />
            )}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={sectionHeading}>Filtering</div>
            <Grid container spacing={2} style={{ marginTop: '4px' }}>
              <Grid item xs={6}>
                <Controller
                  name={`${configPrefix}.limit`}
                  control={control}
                  render={({ field }) => (
                    <SydLabel label='Limit' description='Maximum tasks to display (default 5)'>
                      <SydInput
                        type='number'
                        value={field.value ?? ''}
                        onChange={(e) => {
                          const raw = e.target.value
                          if (!raw || !raw.trim()) { field.onChange(null); return }
                          const parsed = parseInt(raw, 10)
                          field.onChange(isNaN(parsed) ? null : parsed)
                        }}
                        placeholder='5'
                      />
                    </SydLabel>
                  )}
                />
              </Grid>
              {showUpdatedWithin && (
                <Grid item xs={6}>
                  <Controller
                    name={`${configPrefix}.updatedWithinDays`}
                    control={control}
                    render={({ field }) => (
                      <SydLabel label='Updated Within (days)'>
                        <SydInput
                          type='number'
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value
                            if (!raw || !raw.trim()) { field.onChange(null); return }
                            const parsed = parseInt(raw, 10)
                            field.onChange(isNaN(parsed) ? null : parsed)
                          }}
                          placeholder='7'
                        />
                      </SydLabel>
                    )}
                  />
                </Grid>
              )}
              {showStaleAfter && (
                <Grid item xs={6}>
                  <Controller
                    name={`${configPrefix}.staleAfterDays`}
                    control={control}
                    render={({ field }) => (
                      <SydLabel label='Stale After (days)'>
                        <SydInput
                          type='number'
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value
                            if (!raw || !raw.trim()) { field.onChange(null); return }
                            const parsed = parseInt(raw, 10)
                            field.onChange(isNaN(parsed) ? null : parsed)
                          }}
                          placeholder='30'
                        />
                      </SydLabel>
                    )}
                  />
                </Grid>
              )}
              {showLookahead && (
                <Grid item xs={6}>
                  <Controller
                    name={`${configPrefix}.lookaheadDays`}
                    control={control}
                    render={({ field }) => (
                      <SydLabel label='Lookahead (days)' description='How many days forward to fetch (default 90)'>
                        <SydInput
                          type='number'
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value
                            if (!raw || !raw.trim()) { field.onChange(null); return }
                            const parsed = parseInt(raw, 10)
                            field.onChange(isNaN(parsed) ? null : parsed)
                          }}
                          placeholder='90'
                        />
                      </SydLabel>
                    )}
                  />
                </Grid>
              )}
              {showOrder && (
                <Grid item xs={6}>
                  <Controller
                    name={`${configPrefix}.order`}
                    control={control}
                    render={({ field }) => (
                      <SydLabel label='Sort Order' description='Server-side sort applied by Wealthbox'>
                        <SydSelect
                          options={orderOptions}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value || null)}
                        />
                      </SydLabel>
                    )}
                  />
                </Grid>
              )}
              {showTaskFilters && (
                <Grid item xs={6}>
                  <Controller
                    name={`${configPrefix}.taskType`}
                    control={control}
                    render={({ field }) => (
                      <SydLabel label='Task Type'>
                        <SydSelect
                          options={taskTypeOptions}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value || null)}
                        />
                      </SydLabel>
                    )}
                  />
                </Grid>
              )}
              {showTaskFilters && (
                <Grid item xs={6}>
                  <Controller
                    name={`${configPrefix}.createdByScope`}
                    control={control}
                    render={({ field }) => (
                      <SydLabel label='Created By'>
                        <SydSelect
                          options={createdByScopeOptions}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value || null)}
                        />
                      </SydLabel>
                    )}
                  />
                </Grid>
              )}
              {showTaskFilters && (
                <Grid item xs={6}>
                  <Controller
                    name={`${configPrefix}.teamId`}
                    control={control}
                    render={({ field }) => (
                      <SydLabel label='Team ID (optional)' description='Wealthbox team id; blank = no team filter'>
                        <SydInput
                          type='number'
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value
                            if (!raw || !raw.trim()) { field.onChange(null); return }
                            const parsed = parseInt(raw, 10)
                            field.onChange(isNaN(parsed) ? null : parsed)
                          }}
                          placeholder='e.g. 12'
                        />
                      </SydLabel>
                    )}
                  />
                </Grid>
              )}
              {showPreviewLength && (
                <Grid item xs={6}>
                  <Controller
                    name={`${configPrefix}.previewLength`}
                    control={control}
                    render={({ field }) => (
                      <SydLabel label='Preview Length' description='Characters of note body shown in the row (default 200)'>
                        <SydInput
                          type='number'
                          value={field.value ?? ''}
                          onChange={(e) => {
                            const raw = e.target.value
                            if (!raw || !raw.trim()) { field.onChange(null); return }
                            const parsed = parseInt(raw, 10)
                            field.onChange(isNaN(parsed) ? null : parsed)
                          }}
                          placeholder='200'
                        />
                      </SydLabel>
                    )}
                  />
                </Grid>
              )}
              {showTaskFilters && (
                <Grid item xs={6}>
                  <Controller
                    name={`${configPrefix}.includeCompleted`}
                    control={control}
                    render={({ field }) => (
                      <label className='flex items-center gap-2' style={{ minHeight: '54px', cursor: 'pointer' }}>
                        <SydSwitch
                          label='Include completed'
                          value={!!field.value}
                          onChange={(_event, newValue) => field.onChange(newValue)}
                        />
                        <span className='font-size-sm'>Include completed</span>
                      </label>
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </div>
        </div>
      </Grid>
    </Grid>
  )
}

WealthboxListConfigEditor.propTypes = {
  fieldPrefix: PropTypes.string.isRequired
}

export default WealthboxListConfigEditor
